/**
 * API client for the geoVINUM Experience B2B module.
 *
 * The participant and staff sessions live in their own opaque tokens, kept in
 * localStorage under dedicated keys so they never collide with the main JWT.
 */
import axios from "axios";
import { API } from "@/lib/api";

const PARTICIPANT_TOKEN = "geovinum_participant_token";
const PARTICIPANT_CTX = "geovinum_participant_ctx";
const STAFF_TOKEN = "geovinum_staff_token";
const STAFF_CTX = "geovinum_staff_ctx";

export const participantAuth = {
  getToken: () => localStorage.getItem(PARTICIPANT_TOKEN),
  setToken: (t) => {
    localStorage.setItem(PARTICIPANT_TOKEN, t);
    try { window.dispatchEvent(new StorageEvent("storage", { key: PARTICIPANT_TOKEN })); } catch {}
  },
  getCtx: () => { try { return JSON.parse(localStorage.getItem(PARTICIPANT_CTX) || "null"); } catch { return null; } },
  setCtx: (c) => localStorage.setItem(PARTICIPANT_CTX, JSON.stringify(c)),
  clear: () => {
    localStorage.removeItem(PARTICIPANT_TOKEN); localStorage.removeItem(PARTICIPANT_CTX);
    try { window.dispatchEvent(new StorageEvent("storage", { key: PARTICIPANT_TOKEN })); } catch {}
  },
};

export const staffAuth = {
  getToken: () => localStorage.getItem(STAFF_TOKEN),
  setToken: (t) => localStorage.setItem(STAFF_TOKEN, t),
  getCtx: () => { try { return JSON.parse(localStorage.getItem(STAFF_CTX) || "null"); } catch { return null; } },
  setCtx: (c) => localStorage.setItem(STAFF_CTX, JSON.stringify(c)),
  clear: () => { localStorage.removeItem(STAFF_TOKEN); localStorage.removeItem(STAFF_CTX); },
};

// Bare axios (no interceptor auto-attaching main JWT).
function _client(token) {
  return axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

// ─────────── Public / participant ───────────
export const xpApi = {
  publicEvents: () => _client().get("/experience/events").then(r => r.data),
  publicEventDetail: (slug) => _client().get(`/experience/events/${slug}`).then(r => r.data),
  checkout: (payload) => _client().post("/experience/checkout", payload).then(r => r.data),
  orderStatus: (id) => _client().get(`/experience/orders/${id}`).then(r => r.data),

  participantLogin: (code) =>
    _client().post("/experience/participant/login", { participant_code: code }).then(r => r.data),
  participantClaim: (payload) =>
    _client(participantAuth.getToken()).post("/experience/participant/claim", payload).then(r => r.data),

  me: () => _client(participantAuth.getToken()).get("/experience/me").then(r => r.data),
  listWines: () => _client(participantAuth.getToken()).get("/experience/wines").then(r => r.data),
  requestTastingToken: (wineId) =>
    _client(participantAuth.getToken()).post(`/experience/wines/${wineId}/token`).then(r => r.data),
  markDiscovered: (wineId) =>
    _client(participantAuth.getToken()).post(`/experience/wines/${wineId}/discover`).then(r => r.data),
  submitQuiz: (payload) =>
    _client(participantAuth.getToken()).post("/experience/wines/quiz-result", payload).then(r => r.data),
  listTeams: () => _client(participantAuth.getToken()).get("/experience/teams").then(r => r.data),
  createTeam: (payload) =>
    _client(participantAuth.getToken()).post("/experience/teams", payload).then(r => r.data),
  joinTeam: (payload) =>
    _client(participantAuth.getToken()).post("/experience/teams/join", payload).then(r => r.data),
  leaderboardIndividual: () =>
    _client(participantAuth.getToken()).get("/experience/leaderboard/individual").then(r => r.data),
  leaderboardTeams: () =>
    _client(participantAuth.getToken()).get("/experience/leaderboard/teams").then(r => r.data),

  toggleFavorite: (wineId) =>
    _client(participantAuth.getToken()).post(`/experience/wines/${wineId}/favorite`).then(r => r.data),

  // Notes de dégustation (Iter65)
  saveTastingNotes: (wineId, notes) =>
    _client(participantAuth.getToken()).post(`/experience/wines/${wineId}/notes`, notes).then(r => r.data),
  getTastingNotes: (wineId) =>
    _client(participantAuth.getToken()).get(`/experience/wines/${wineId}/notes`).then(r => r.data),
  transcribeNotes: (wineId, audioBlob) => {
    const fd = new FormData();
    fd.append("audio", audioBlob, "note.webm");
    return _client(participantAuth.getToken()).post(
      `/experience/wines/${wineId}/notes/transcribe`, fd,
      { headers: { "Content-Type": "multipart/form-data" } }
    ).then(r => r.data);
  },
  publicLiveLeaderboard: (slug) =>
    api.get(`/experience/events/${slug}/leaderboard-live`).then(r => r.data),

  // Addons (bottles + merch)
  listAddons: () => _client(participantAuth.getToken()).get("/experience/addons").then(r => r.data),
  addonCheckout: (payload) =>
    _client(participantAuth.getToken()).post("/experience/addons/checkout", payload).then(r => r.data),
  addonOrderStatus: (id) =>
    _client(participantAuth.getToken()).get(`/experience/addons/orders/${id}`).then(r => r.data),
};

// ─────────── Staff ───────────
export const staffXpApi = {
  login: (event_id, pin) => _client().post("/staff/login", { event_id, pin }).then(r => r.data),
  me: () => _client(staffAuth.getToken()).get("/staff/me").then(r => r.data),
  scan: (token) => _client(staffAuth.getToken()).post("/staff/scan", { token }).then(r => r.data),
};

// ─────────── Pro (JWT-based, uses main axios interceptor) ───────────
import { api } from "@/lib/api";

export const proXpApi = {
  listOrgs: () => api.get("/pro/orgs").then(r => r.data),
  createOrg: (payload) => api.post("/pro/orgs", payload).then(r => r.data),
  getOrg: (id) => api.get(`/pro/orgs/${id}`).then(r => r.data),
  updateOrg: (id, patch) => api.patch(`/pro/orgs/${id}`, patch).then(r => r.data),

  stripeOnboard: (orgId, payload) =>
    api.post(`/pro/stripe/${orgId}/onboard`, payload).then(r => r.data),
  stripeStatus: (orgId) => api.get(`/pro/stripe/${orgId}/status`).then(r => r.data),

  listEvents: (orgId) => api.get(`/pro/events/${orgId}`).then(r => r.data),
  createEvent: (orgId, payload) => api.post(`/pro/events/${orgId}`, payload).then(r => r.data),
  getEvent: (id) => api.get(`/pro/events/detail/${id}`).then(r => r.data),
  updateEvent: (id, patch) => api.patch(`/pro/events/detail/${id}`, patch).then(r => r.data),
  publishEvent: (id) => api.post(`/pro/events/detail/${id}/publish`).then(r => r.data),
  deleteEvent: (id) => api.delete(`/pro/events/detail/${id}`).then(r => r.data),

  listTicketTypes: (id) => api.get(`/pro/events/detail/${id}/ticket-types`).then(r => r.data),
  createTicketType: (id, payload) =>
    api.post(`/pro/events/detail/${id}/ticket-types`, payload).then(r => r.data),
  updateTicketType: (eid, ttid, patch) =>
    api.patch(`/pro/events/detail/${eid}/ticket-types/${ttid}`, patch).then(r => r.data),
  deleteTicketType: (eid, ttid) =>
    api.delete(`/pro/events/detail/${eid}/ticket-types/${ttid}`).then(r => r.data),

  listSlots: (id) => api.get(`/pro/events/detail/${id}/slots`).then(r => r.data),
  createSlot: (id, payload) => api.post(`/pro/events/detail/${id}/slots`, payload).then(r => r.data),
  updateSlot: (eid, sid, patch) => api.patch(`/pro/events/detail/${eid}/slots/${sid}`, patch).then(r => r.data),
  deleteSlot: (eid, sid) => api.delete(`/pro/events/detail/${eid}/slots/${sid}`).then(r => r.data),

  listWines: (id) => api.get(`/pro/events/detail/${id}/wines`).then(r => r.data),
  createWine: (id, payload) => api.post(`/pro/events/detail/${id}/wines`, payload).then(r => r.data),
  updateWine: (eid, wid, patch) =>
    api.patch(`/pro/events/detail/${eid}/wines/${wid}`, patch).then(r => r.data),
  deleteWine: (eid, wid) => api.delete(`/pro/events/detail/${eid}/wines/${wid}`).then(r => r.data),
  addInventory: (eid, wid, payload) =>
    api.post(`/pro/events/detail/${eid}/wines/${wid}/inventory/add`, payload).then(r => r.data),

  // Teams (organizer-managed pool participants pick from)
  listEventTeams: (id) => api.get(`/pro/events/detail/${id}/teams`).then(r => r.data),
  createEventTeam: (id, payload) => api.post(`/pro/events/detail/${id}/teams`, payload).then(r => r.data),
  updateEventTeam: (eid, tid, patch) => api.patch(`/pro/events/detail/${eid}/teams/${tid}`, patch).then(r => r.data),
  deleteEventTeam: (eid, tid) => api.delete(`/pro/events/detail/${eid}/teams/${tid}`).then(r => r.data),

  // Demo ticket generator (no Stripe)
  createDemoTicket: (id, payload) => api.post(`/pro/events/detail/${id}/demo-ticket`, payload).then(r => r.data),

  // Academy picker
  searchAcademyCourses: (eid, q) =>
    api.get(`/pro/events/detail/${eid}/academy-courses`, { params: { q } }).then(r => r.data),

  // KMS atlas-blocks picker (Iter64)
  searchAtlasBlocks: (eid, q, category) =>
    api.get(`/pro/events/detail/${eid}/atlas-blocks/search`, {
      params: { q, category: category || "" }
    }).then(r => r.data),
  getWineBlocks: (eid, wid) =>
    api.get(`/pro/events/detail/${eid}/wines/${wid}/blocks`).then(r => r.data),

  // Merch (Iter62)
  listMerch: (id) => api.get(`/pro/events/detail/${id}/merch`).then(r => r.data),
  createMerch: (id, payload) => api.post(`/pro/events/detail/${id}/merch`, payload).then(r => r.data),
  updateMerch: (eid, mid, patch) => api.patch(`/pro/events/detail/${eid}/merch/${mid}`, patch).then(r => r.data),
  deleteMerch: (eid, mid) => api.delete(`/pro/events/detail/${eid}/merch/${mid}`).then(r => r.data),
  listAddonOrders: (id) => api.get(`/pro/events/detail/${id}/addon-orders`).then(r => r.data),

  // Palmarès + game control (Iter65)
  startGame: (id) => api.post(`/pro/events/detail/${id}/game/start`).then(r => r.data),
  stopGame: (id) => api.post(`/pro/events/detail/${id}/game/stop`).then(r => r.data),
  getLeaderboard: (id) => api.get(`/pro/events/detail/${id}/leaderboard`).then(r => r.data),

  listOrders: (id) => api.get(`/pro/events/detail/${id}/orders`).then(r => r.data),
  listTickets: (id) => api.get(`/pro/events/detail/${id}/tickets`).then(r => r.data),

  inviteStaff: (id, payload) => api.post(`/staff/invite/${id}`, payload).then(r => r.data),
  listStaff: (id) => api.get(`/staff/list/${id}`).then(r => r.data),

  // Analytics
  eventKPIs: (id) => api.get(`/pro/events/analytics/${id}/kpis`).then(r => r.data),

  // Gamification
  listRules: (id) => api.get(`/pro/events/gamification/${id}/rules`).then(r => r.data),
  upsertRule: (id, payload) => api.put(`/pro/events/gamification/${id}/rules`, payload).then(r => r.data),

  // Admin (GeoVINUM super-admin)
  adminAllOrgs: () => api.get("/admin/experience/orgs").then(r => r.data),
  adminOrgDetail: (id) => api.get(`/admin/experience/orgs/${id}`).then(r => r.data),
  adminGlobalKPIs: () => api.get("/admin/experience/kpis").then(r => r.data),
  adminGetGlobalCommission: () => api.get("/admin/experience/commission/global").then(r => r.data),
  adminSetGlobalCommission: (payload) =>
    api.put("/admin/experience/commission/global", payload).then(r => r.data),
  adminSetOrgCommission: (id, payload) =>
    api.patch(`/admin/experience/orgs/${id}/commission`, payload).then(r => r.data),
};

export function formatCents(cents, currency = "EUR") {
  const v = (Number(cents) || 0) / 100;
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency }).format(v);
}
